import { getPlanInfo, PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import {
  PROMOTION_SUBSCRIPTION_PLAN_CODE,
  SubscriptionTabDefinition,
  TeamMessageStatistics,
  TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE,
} from '@mm/state/helpers/billing-subscription-tab-helpers';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { EnhancedOutgoingMessagesStatistic } from '@mm/state/model/ConnectedPageModel';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import { getPercentage } from '@mm/utils';

export const getShowTeamOverageArea = (
  teamMessageStatistics?: TeamMessageStatistics,
  teamSubscription?: SubscriptionModel,
) => {
  if (teamMessageStatistics && teamSubscription) {
    const overMessageLimit =
      teamMessageStatistics.used >= teamMessageStatistics.messagesLimit;

    return overMessageLimit && !teamSubscription.overagesEnabled;
  }

  return false;
};

export const getIsApproachingCreditsLimit = (
  subscription?: SubscriptionModel,
  teamMessageStatistics?: TeamMessageStatistics,
  utilizationPercentage?: number,
) => {
  if (subscription && !subscription.overagesEnabled) {
    return false;
  }

  if (teamMessageStatistics) {
    const usedPercent = getPercentage(
      teamMessageStatistics.used,
      teamMessageStatistics.messagesLimit,
    );

    return usedPercent > 79 && usedPercent < 100;
  }

  if (utilizationPercentage) {
    return utilizationPercentage > 79 && utilizationPercentage < 100;
  }

  return false;
};

export const getBillingPlanForTab = (
  planTab: SubscriptionTabDefinition,
): PageBillingType | undefined => {
  if (planTab.plan) {
    return planTab.plan.pageBillingType;
  }
  if (planTab.planCode === PROMOTION_SUBSCRIPTION_PLAN_CODE) {
    return PageBillingType.Promotion;
  }
  if (planTab.planCode === TEAM_TRIAL_SUBSCRIPTION_PLAN_CODE) {
    return PageBillingType.TrialTeam;
  }
};

export const planCanManageOverages = (
  limit: number | null | undefined,
  planInfo: ReturnType<typeof getPlanInfo>,
) =>
  !(
    limit === null ||
    limit === undefined ||
    planInfo.isFreePlan ||
    planInfo.isSpecialPlan ||
    planInfo.isAnyTeamPlan ||
    planInfo.isInstachamp
  );

export const getOverageText = (
  planInfo: Partial<PlanInfo>,
  isFreeTrialPromotion: boolean,
  isApproachingLimit?: boolean,
  isNewBillingSubscription?: boolean,
) => {
  if (isFreeTrialPromotion) {
    return 'Upgrade to continue sending messages';
  }
  if (planInfo.isFreePlan) {
    return 'Upgrade to get more sends for this page';
  }

  if (planInfo.isProPlan) {
    return 'To continue sending messages, upgrade your plan to Startup';
  }

  if (isApproachingLimit) {
    return 'Upgrade now or enable overage payments to avoid an interruption in service.';
  }

  if (isNewBillingSubscription) {
    return 'To continue using Customers.ai upgrade your plan or enable overage payments';
  }

  return 'To continue sending messages, upgrade your plan or enable overage payments';
};

export const getUsedAndTotal = (
  statistics: TeamMessageStatistics | EnhancedOutgoingMessagesStatistic,
  subscription?: SubscriptionModel,
) => {
  let sentAllotment = 0;
  if (subscription && 'maxSendAllotment' in subscription) {
    sentAllotment = subscription.maxSendAllotment;
  }
  if (statistics && 'utilizationPercentage' in statistics) {
    return [
      (statistics as EnhancedOutgoingMessagesStatistic).count,
      sentAllotment ||
        (statistics as EnhancedOutgoingMessagesStatistic).outgoingMessageLimit,
    ];
  }
  return [
    (statistics as TeamMessageStatistics)?.used,
    sentAllotment || (statistics as TeamMessageStatistics)?.messagesLimit,
  ];
};

export const getBasePath = route => {
  const pathParts = route.split('/');
  const filteredPathParts = pathParts.filter(part => !part.startsWith(':'));
  const basePath = filteredPathParts.join('/');
  return basePath;
};
